import React, { useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import styles from "./BookingPanel.module.css"

const MONTHS = Array.from({ length: 12 }, (_, index) => index)
// The first day of a month (setting the month on today's date would overflow on the 29th–31st).
const monthStart = (year, monthIndex) => dayjs(new Date(year, monthIndex, 1))

const NavButton = ({ label, iconName, isDisabled, onClick }) => (
  <Button
    shape="circle"
    size="small"
    className={styles.calendarNavButton}
    aria-label={label}
    disabled={isDisabled}
    onClick={onClick}
    icon={<Icon type="feather" name={iconName} width={16} height={16} color="currentColor" />}
  />
)

NavButton.propTypes = {
  label: PropTypes.string.isRequired,
  iconName: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired
}

NavButton.defaultProps = {
  isDisabled: false
}

// "‹  August 2026 ⌄  ›" above the booking calendar. The title opens a month grid for a year
// ("‹ 2026 ›", Jan–Dec): months before `firstMonth` can't be chosen, and picking one shows it in
// the calendar. While the grid is open it replaces the days (`children`).
const CalendarHeader = ({ month, firstMonth, onMonthChange, children }) => {
  const { t } = useTranslation()
  const [gridYear, setGridYear] = useState(null)
  const isGridOpen = gridYear != null
  const shown = dayjs(month).startOf("month")
  const earliest = dayjs(firstMonth).startOf("month")

  const toggleGrid = () => setGridYear(isGridOpen ? null : shown.year())
  const chooseMonth = (monthIndex) => {
    onMonthChange(monthStart(gridYear, monthIndex).toDate())
    setGridYear(null)
  }

  return (
    <Flex vertical className={styles.calendarBox}>
      <Flex align="center" justify="space-between" className={styles.calendarHeader}>
        <NavButton
          label={t("booking_widget.experience.calendar.previous_month")}
          iconName="ChevronLeft"
          isDisabled={isGridOpen || !shown.isAfter(earliest)}
          onClick={() => onMonthChange(shown.subtract(1, "month").toDate())}
        />
        <button
          type="button"
          className={styles.calendarTitle}
          aria-expanded={isGridOpen}
          aria-label={t("booking_widget.experience.calendar.choose_month", { month: shown.format("MMMM YYYY") })}
          onClick={toggleGrid}
        >
          {shown.format("MMMM YYYY")}
          <Icon type="feather" name={isGridOpen ? "ChevronUp" : "ChevronDown"} width={16} height={16} color="currentColor" />
        </button>
        <NavButton
          label={t("booking_widget.experience.calendar.next_month")}
          iconName="ChevronRight"
          isDisabled={isGridOpen}
          onClick={() => onMonthChange(shown.add(1, "month").toDate())}
        />
      </Flex>
      {isGridOpen ? (
        <Flex vertical className={styles.monthGrid}>
          <Flex align="center" justify="space-between" className={styles.calendarHeader}>
            <NavButton
              label={t("booking_widget.experience.calendar.previous_year")}
              iconName="ChevronLeft"
              isDisabled={gridYear <= earliest.year()}
              onClick={() => setGridYear(gridYear - 1)}
            />
            <span className={styles.monthGridYear}>{gridYear}</span>
            <NavButton label={t("booking_widget.experience.calendar.next_year")} iconName="ChevronRight" onClick={() => setGridYear(gridYear + 1)} />
          </Flex>
          <div className={styles.monthGridMonths}>
            {MONTHS.map((monthIndex) => {
              const candidate = monthStart(gridYear, monthIndex)
              const isSelected = candidate.isSame(shown, "month")
              return (
                <button
                  key={monthIndex}
                  type="button"
                  className={`${styles.monthGridMonth} ${isSelected ? styles.monthGridMonthSelected : ""}`}
                  aria-pressed={isSelected}
                  disabled={candidate.isBefore(earliest, "month")}
                  onClick={() => chooseMonth(monthIndex)}
                >
                  {candidate.format("MMM")}
                </button>
              )
            })}
          </div>
        </Flex>
      ) : (
        children
      )}
    </Flex>
  )
}

CalendarHeader.propTypes = {
  month: PropTypes.instanceOf(Date).isRequired,
  firstMonth: PropTypes.instanceOf(Date).isRequired,
  onMonthChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
}

export default CalendarHeader
